import React from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { useEffect, useState } from "react";
import {
  House,
  CalendarPlus,
  ListChecks,
  UserCheck,
  SignOut,
  Package,
  ShieldCheck,
  Books,
  Database,
  Warning,
  Truck,
  List as MenuIcon,
  X as CloseIcon,
  CaretDoubleLeft,
  CaretDoubleRight,
} from "@phosphor-icons/react";
import { InstallButton } from "@/components/InstallPrompt";
import { usePerms } from "@/lib/perms";

const linkBase =
  "flex items-center gap-3 px-4 py-2.5 rounded-sm text-sm transition-colors duration-200 group relative";
const linkIdle = "text-white/60 hover:bg-white/5 hover:text-white";
const linkActive = "bg-[#0055FF]/15 text-white border-l-2 border-[#0055FF]";

function SideLink({ to, icon: Icon, label, badge, testId, onClick, collapsed }) {
  return (
    <NavLink
      to={to}
      data-testid={testId}
      onClick={onClick}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        `${linkBase} ${isActive ? linkActive : linkIdle} ${collapsed ? "justify-center px-2" : ""}`
      }
    >
      <Icon size={20} weight="duotone" className="shrink-0" />
      {!collapsed && <span className="flex-1 truncate">{label}</span>}
      {badge > 0 && (
        collapsed ? (
          <span
            data-testid="pending-approvals-badge"
            className="absolute top-1 right-1 w-2 h-2 rounded-full bg-[#FACC15]"
          />
        ) : (
          <span
            data-testid="pending-approvals-badge"
            className="font-mono-data text-[10px] px-1.5 py-0.5 rounded-sm bg-[#FACC15] text-black font-bold"
          >
            {badge}
          </span>
        )
      )}
    </NavLink>
  );
}

const COLLAPSED_KEY = "rcn_sidebar_collapsed";

export default function Layout() {
  const { user, logout } = useAuth();
  const perms = usePerms();
  const nav = useNavigate();
  const location = useLocation();
  const [pending, setPending] = useState(0);
  const [pendingToday, setPendingToday] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSED_KEY) === "1"; } catch (err) { console.debug("[Layout] localStorage read failed", err); return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0"); } catch (err) { console.debug("[Layout] localStorage write failed", err); }
  }, [collapsed]);

  useEffect(() => {
    let alive = true;
    const loadPendingUsers = async () => {
      if (user?.role !== "admin") return;
      try {
        const { data } = await api.get("/users", { params: { status: "pending" } });
        if (alive) setPending(data.length);
      } catch (err) {
        console.debug("[Layout] loadPendingUsers failed", err);
      }
    };
    const loadPendingToday = async () => {
      try {
        const { data } = await api.get("/dashboard/stats");
        if (alive) setPendingToday(data.pending_today || 0);
      } catch (err) {
        console.debug("[Layout] loadPendingToday failed", err);
      }
    };
    loadPendingUsers();
    loadPendingToday();
    const t = setInterval(() => {
      loadPendingUsers();
      loadPendingToday();
    }, 20000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [user]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const closeDrawer = () => setMobileOpen(false);

  const renderSidebar = (isMobile) => {
    const isCollapsed = !isMobile && collapsed;
    const width = isCollapsed ? "w-16" : "w-64";
    return (
      <div className={`${width} shrink-0 h-full bg-[#0A0A0A] flex flex-col border-r border-white/10 transition-[width] duration-200`}>
        <div className={`px-3 py-5 border-b border-white/10 flex items-center gap-3 ${isCollapsed ? "justify-center" : "px-5"}`}>
          <div className="w-9 h-9 rounded-sm bg-[#0055FF] flex items-center justify-center shrink-0">
            <Package size={20} weight="fill" color="#fff" />
          </div>
          {!isCollapsed && (
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg leading-tight">WAREHOUSE</div>
              <div className="font-mono-data text-[10px] text-white/50 tracking-widest">
                RCN · CONTROL
              </div>
            </div>
          )}
          {isMobile && (
            <button
              data-testid="sidebar-close"
              onClick={closeDrawer}
              className="p-1.5 rounded-sm text-white/50 hover:bg-white/5"
            >
              <CloseIcon size={18} />
            </button>
          )}
        </div>

        <nav className={`flex-1 py-3 space-y-1 overflow-y-auto ${isCollapsed ? "px-2" : "px-3"}`}>
          <SideLink to="/dashboard" icon={House} label="Dashboard" testId="nav-dashboard" onClick={closeDrawer} collapsed={isCollapsed} />
          {perms.has("appointments:create") && (
            <SideLink to="/citas/nueva" icon={CalendarPlus} label="Nueva Cita" testId="nav-nueva-cita" onClick={closeDrawer} collapsed={isCollapsed} />
          )}
          <SideLink
            to="/citas"
            icon={ListChecks}
            label="Control de Citas"
            badge={pendingToday}
            testId="nav-control-citas"
            onClick={closeDrawer}
            collapsed={isCollapsed}
          />
          <SideLink
            to="/base-de-datos"
            icon={Database}
            label="Base de Datos"
            testId="nav-base-datos"
            onClick={closeDrawer}
            collapsed={isCollapsed}
          />
          <SideLink
            to="/rechazos"
            icon={Warning}
            label="Rechazos"
            testId="nav-rechazos"
            onClick={closeDrawer}
            collapsed={isCollapsed}
          />
          <SideLink
            to="/recolecciones"
            icon={Truck}
            label="Recolecciones"
            testId="nav-recolecciones"
            onClick={closeDrawer}
            collapsed={isCollapsed}
          />
          {user?.role === "admin" && (
            <SideLink
              to="/admin/aprobaciones"
              icon={UserCheck}
              label="Aprobación de Usuarios"
              badge={pending}
              testId="nav-aprobaciones"
              onClick={closeDrawer}
              collapsed={isCollapsed}
            />
          )}
          {user?.role === "admin" && (
            <SideLink to="/admin/catalogos" icon={Books} label="Catálogos" testId="nav-catalogos" onClick={closeDrawer} collapsed={isCollapsed} />
          )}
        </nav>

        <div className={`border-t border-white/10 space-y-2 ${isCollapsed ? "p-2" : "p-3"}`}>
          {!isCollapsed && <InstallButton className="w-full justify-center" />}
          <div className={`flex items-center gap-3 py-2 ${isCollapsed ? "px-0 justify-center" : "px-3"}`}>
            <div className="w-8 h-8 rounded-sm bg-white/10 flex items-center justify-center shrink-0" title={user?.username}>
              {user?.role === "admin" ? (
                <ShieldCheck size={16} color="#FACC15" weight="fill" />
              ) : (
                <UserCheck size={16} color="#9CA3AF" />
              )}
            </div>
            {!isCollapsed && (
              <div className="min-w-0">
                <div className="text-sm font-medium truncate" data-testid="current-username">
                  {user?.username}
                </div>
                <div className="text-[10px] font-mono-data uppercase text-white/40 tracking-wider">
                  {user?.role}
                </div>
              </div>
            )}
          </div>
          <button
            data-testid="logout-btn"
            onClick={async () => {
              await logout();
              nav("/login");
            }}
            title={isCollapsed ? "Cerrar sesión" : undefined}
            className={`w-full flex items-center gap-2 py-2 rounded-sm text-sm text-white/70 hover:bg-white/5 hover:text-white transition-colors ${isCollapsed ? "justify-center px-0" : "px-3"}`}
          >
            <SignOut size={16} />
            {!isCollapsed && "Cerrar sesión"}
          </button>
          {!isMobile && (
            <button
              data-testid="sidebar-toggle"
              onClick={() => setCollapsed((v) => !v)}
              title={isCollapsed ? "Expandir" : "Colapsar"}
              className={`w-full hidden md:flex items-center gap-2 py-2 rounded-sm text-xs text-white/50 hover:bg-white/5 hover:text-white transition-colors ${isCollapsed ? "justify-center px-0" : "px-3"}`}
            >
              {isCollapsed ? <CaretDoubleRight size={14} /> : <CaretDoubleLeft size={14} />}
              {!isCollapsed && "Colapsar menú"}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex bg-[#050505] text-[#F3F4F6] relative z-10" style={{ minHeight: "100dvh" }}>
      {/* Desktop sidebar */}
      <aside className="hidden md:block sticky top-0 h-screen">{renderSidebar(false)}</aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 bg-black/70 backdrop-blur-sm z-40 fade-up"
            onClick={closeDrawer}
          />
          <aside className="md:hidden fixed inset-y-0 left-0 z-50 fade-up">{renderSidebar(true)}</aside>
        </>
      )}

      {/* Main */}
      <main className="flex-1 min-w-0">
        {/* Mobile top bar */}
        <div
          className="md:hidden sticky top-0 z-30 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-white/10 flex items-center gap-3 px-4 py-3"
          style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}
        >
          <button
            data-testid="mobile-menu"
            onClick={() => setMobileOpen(true)}
            className="p-1.5 rounded-sm text-white/70 hover:bg-white/5"
          >
            <MenuIcon size={22} />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-sm bg-[#0055FF] flex items-center justify-center">
              <Package size={14} weight="fill" color="#fff" />
            </div>
            <div className="font-display text-sm">WAREHOUSE RCN</div>
          </div>
        </div>

        <div
          className="p-3 sm:p-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto"
          style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
        >
          <Outlet />
        </div>
      </main>
    </div>
  );
}
